import { useEffect, useRef, useState } from "react";

import NoteModel from "../../../api/models/NoteModel";
import SessionModel from "../../../api/models/SessionModel";
import { showTutorSessions } from "../../../api/endpoints/tutors/sessions";
import { useNavigate, useParams } from "react-router-dom";
import { indexStudentNotes } from "../../../api/endpoints/tutors/notes";

import QuranViewer from "../../../components/quran-viewer";
import { makeApiRequest } from "../../../helpers";

import { ApiResponse } from "../../../api/apiResponse";

import IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";

import {
  BookOpenIcon,
  ChatAltIcon,
  InformationCircleIcon,
  PhoneMissedCallIcon,
} from "@heroicons/react/solid";
import LoadingSpinner from "../../../components/LoadingSpinner";
import SessionNotes from "../../../components/sessions/NotesAndSkills/SessionsNotes";
import SessionInfo from "../../../components/sessions/SessionInfo";
import UserSessionRoom from "../../../components/sessions/UserSessionRoom";
import EndSession from "../../../components/modal/views/EndSession";
import {
  createSkills,
  indexCandidateSkills,
} from "../../../api/endpoints/tutors/skills";
import { useQuery } from "react-query";
import EndCandidateSession from "../../../components/modal/views/EndCandidateSession";
import { useAuth } from "../../../context/AuthContext";
import EndSessionNewBoarding from "../../../components/modal/views/EndSessionNewBoarding";
import ExistConfirmationModal from "../../../components/modal/ExistSessionModal";
import { SkillsParamsType } from "../../../api/@types/globalTypes";

export default function ReadingRoom() {
  const [showTrailingContent, setShowTrailingContent] = useState(false);
  const [endCallModal, setEndCallModal] = useState(false);
  const [trailingContentIndex, setTrailingContentIndex] = useState(0);
  const [session, setSession] = useState<null | SessionModel>(null);
  const navigate = useNavigate();
  const { userType }: any = useAuth();
  const [pageNumber, setPageNumber] = useState<undefined | number>(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { id } = useParams();
  const jitsiApiRef = useRef<IJitsiMeetExternalApi>(null);

  const getUserSession = async () => {
    const userSessionApi = await showTutorSessions(id!, "meeting_room");
    if (userSessionApi.isSuccess) {
      const userSession: SessionModel = SessionModel.fromApiResponse(
        userSessionApi.data
      );
      setSession(userSession);
      setPageNumber(userSession!.lastQuranPageReached! ?? 1);
    }
  };

  const listAndSetStudentNotes = (
    orderBy: any,
    setNotes: any,
    noteState: any
  ) => {
    let apiParams: { [key: string]: any } = {};
    if (!!session?.isCandidateSession)
      apiParams.candidate_id = session?.student?.id;
    else apiParams.student_id = session?.student?.id;
    if (!!orderBy) apiParams["order_by"] = orderBy;
    if (!!noteState) apiParams["state"] = noteState;
    makeApiRequest(
      () => indexStudentNotes(apiParams),
      (response: ApiResponse) => {
        setNotes(NoteModel.fromApiResponse(response.data));
      }
    );
  };

  const { data: finishedSkills, refetch: refetchSkills } = useQuery(
    ["tutorCandidateSkills", session?.student?.id],
    ({ queryKey }) => {
      const params: SkillsParamsType = {};
      if (!!session?.isCandidateSession) {
        params["candidate_id"] = queryKey[1]!;
      } else {
        params["student_id"] = queryKey[1]!;
      }
      return indexCandidateSkills(params);
    },
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: false,
      select: (response) => {
        let skills: string[] | undefined = undefined;
        if (response.isSuccess) {
          skills = response.data?.skills;
        }
        return skills;
      },
    }
  );

  const handleUpdateSkills = async (finishedSkills: string[]) => {
    const params: SkillsParamsType = {};
    if (!!session?.isCandidateSession) {
      params["candidate_id"] = session?.student?.id!;
    } else {
      params["student_id"] = session?.student?.id!;
    }
    const response = await createSkills(params, finishedSkills);
    if (!!response.isSuccess) {
      refetchSkills();
      return true;
    } else {
      return false;
    }
  };

  const fetchSessionAndSkills = async () => {
    await getUserSession();
    if (!session?.student?.isLearner) {
      refetchSkills();
    }
  };

  useEffect(() => {
    fetchSessionAndSkills();
  }, []);

  const viewsSize = [
    { sm: 40, md: 55, max: "360px" },
    { sm: 40, md: 50, max: "640px" },
    { sm: 40, md: 50, max: "640px" },
  ];
  const views = [
    <div className=" sm:h-screen h-full ltr:sm:ml-auto rtl:sm:mr-auto">
      {!!session ? (
        <SessionInfo
          session={session}
          onClose={() => {
            setShowTrailingContent(false);
          }}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
    <div className="sm:h-screen h-full ltr:sm:ml-auto rtl:sm:mr-auto">
      {!!session ? (
        <QuranViewer
          onClose={(val: number | undefined) => {
            setShowTrailingContent(false);
            setTimeout(() => {
              setTrailingContentIndex(0);
            }, 10);
            setPageNumber(val);
          }}
          startPage={!!pageNumber ? pageNumber : 1}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
    <div className=" overflow-y-hidden h-full ltr:sm:ml-auto rtl:sm:mr-auto">
      {!!session ? (
        <SessionNotes
          listAndSetStudentNotes={listAndSetStudentNotes}
          isEditable={true}
          sessionId={session.id}
          notes={session.notes!}
          onClose={() => {
            setShowTrailingContent(false);
          }}
          showSkills={true}
          finishedSkills={finishedSkills}
          handleUpdateSkills={handleUpdateSkills}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
  ];
  const navigationItems = [
    {
      Icon: InformationCircleIcon,
      action: () => showContent(0),
      color: "white",
      iconColor: "gray-600",
    },
    {
      Icon: BookOpenIcon,
      action: () => showContent(1),
      color: "white",
      iconColor: "gray-600",
    },
    {
      Icon: ChatAltIcon,
      action: () => showContent(2),
      color: "white",
      iconColor: "gray-600",
    },

    {
      Icon: PhoneMissedCallIcon,
      action: () => setEndCallModal(true),
      color: "red-600",
      iconColor: "white",
    },
  ];
  const navigationNewCandidateItems = [
    {
      Icon: InformationCircleIcon,
      action: () => showContent(0),
      color: "white",
      iconColor: "gray-600",
    },
    {
      Icon: BookOpenIcon,
      action: () => showContent(1),
      color: "white",
      iconColor: "gray-600",
    },
    {
      Icon: PhoneMissedCallIcon,
      action: () => setConfirmOpen(true),
      color: "red-600",
      iconColor: "white",
    },
  ];

  const showContent = (index: number) => {
    setShowTrailingContent(false);
    setTrailingContentIndex(index);
    setShowTrailingContent(true);
  };

  const onEndSession = () => {
    jitsiApiRef.current?.executeCommand("hangup");
    navigate(
      `/tutor/dashboard/room/?sessionId=${session?.id}&sessionNumber=${session?.sessionNumber}&touterName=${session?.student?.name}`
    );
  };

  return (
    <>
      {!!session && (
        <UserSessionRoom
          showTrailingContent={showTrailingContent}
          session={session}
          navigationItems={
            session?.student?.isLearner
              ? navigationNewCandidateItems
              : navigationItems
          }
          viewsSize={viewsSize}
          jitsiApiRef={jitsiApiRef}
          views={views}
          userType={userType}
          trailingContentIndex={trailingContentIndex}
          trailingMaxWidth={
            viewsSize[trailingContentIndex].max as "360px" | "640px"
          }
        />
      )}
      {!!session &&
        (session.isCandidateSession ? (
          <EndCandidateSession
            isOpen={endCallModal}
            onClose={() => setEndCallModal(false)}
            session={session}
            onEndSession={onEndSession}
          />
        ) : session?.student?.isLearner ? (
          <EndSessionNewBoarding
            isOpen={endCallModal}
            onClose={() => setEndCallModal(false)}
            session={session}
            onEndSession={onEndSession}
          />
        ) : (
          <EndSession
            open={endCallModal}
            setOpen={setEndCallModal}
            session={session}
            onEndSession={onEndSession}
          />
        ))}
      <ExistConfirmationModal
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setEndCallModal(true);
        }}
      />
    </>
  );
}
